import { useMutation } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { useMe, useSignOut } from '../session.js';
import { LanguageSwitch } from './LanguageSwitch.js';
import { ShieldIcon } from './icons.js';
import { Button, LoadError, Loading, Notice } from './ui.js';

function SkipLink() {
  const { t } = useTranslation();
  return (
    <a
      href="#main"
      className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-10 focus:rounded focus:bg-white focus:px-3 focus:py-2"
    >
      {t('app.skipToContent')}
    </a>
  );
}

function Brand() {
  const { t } = useTranslation();
  return (
    <span className="flex items-center gap-2 text-lg font-bold text-blue-800">
      <ShieldIcon />
      {t('app.name')}
    </span>
  );
}

/** Login, registration and link pages: a narrow card (wide: Quick-Check result). */
export function PublicLayout({ wide = false }: { wide?: boolean }) {
  const me = useMe();
  return (
    <div className="flex min-h-screen flex-col">
      <SkipLink />
      <header className="flex items-center justify-between gap-4 px-4 py-3 sm:px-8">
        <Link to="/">
          <Brand />
        </Link>
        <LanguageSwitch signedIn={Boolean(me.data)} />
      </header>
      <main
        id="main"
        className={`mx-auto w-full ${wide ? 'max-w-2xl' : 'max-w-md'} flex-1 px-4 py-6`}
      >
        <Outlet />
      </main>
    </div>
  );
}

function VerifyBanner() {
  const { t } = useTranslation();
  const resend = useMutation({ mutationFn: () => unwrap(api.POST('/auth/resend-verification')) });
  return (
    <div className="border-b border-amber-300 bg-amber-50 px-4 py-3 text-amber-950 sm:px-8">
      <p>{t('banner.verify')}</p>
      {resend.isSuccess ? (
        <p role="status" className="font-semibold">
          {t('banner.sent')}
        </p>
      ) : (
        <Button variant="link" busy={resend.isPending} onClick={() => resend.mutate()}>
          {t('banner.resend')}
        </Button>
      )}
      {resend.isError ? <p role="alert">{errorMessage(resend.error, t)}</p> : null}
    </div>
  );
}

/** Signed-in area: navigation (two levels at most, PRD §5), banner, content. */
export function AppLayout() {
  const { t } = useTranslation();
  const me = useMe();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const location = useLocation();

  if (me.isPending) return <Loading />;
  if (me.isError)
    return <LoadError message={errorMessage(me.error, t)} onRetry={() => void me.refetch()} />;
  if (!me.data)
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );

  const link = ({ isActive }: { isActive: boolean }) =>
    `inline-flex min-h-11 items-center rounded-md px-3 font-semibold ${isActive ? 'bg-blue-50 text-blue-900' : 'text-slate-800 hover:bg-slate-100'}`;
  return (
    <div className="flex min-h-screen flex-col">
      <SkipLink />
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-8">
          <Link to="/">
            <Brand />
          </Link>
          <nav aria-label={t('nav.label')} className="order-3 w-full sm:order-2 sm:w-auto">
            <ul className="flex flex-wrap gap-1">
              <li>
                <NavLink to="/" end className={link}>
                  {t('nav.overview')}
                </NavLink>
              </li>
              <li>
                <NavLink to="/requests" className={link}>
                  {t('nav.requests')}
                </NavLink>
              </li>
              <li>
                <NavLink to="/notifications" className={link}>
                  {t('nav.notifications')}
                </NavLink>
              </li>
              <li>
                <NavLink to="/billing" className={link}>
                  {t('nav.billing')}
                </NavLink>
              </li>
              <li>
                <NavLink to="/account" className={link}>
                  {t('nav.account')}
                </NavLink>
              </li>
              {me.data.user.isOperator ? (
                <li>
                  <NavLink to="/admin" className={link}>
                    {t('nav.admin')}
                  </NavLink>
                </li>
              ) : null}
            </ul>
          </nav>
          <div className="order-2 flex items-center gap-2 sm:order-3">
            <LanguageSwitch signedIn />
            <Button
              variant="secondary"
              onClick={() =>
                void signOut().then(() => {
                  void navigate('/login');
                })
              }
            >
              {t('nav.logout')}
            </Button>
          </div>
        </div>
      </header>
      {me.data.user.emailVerified ? null : <VerifyBanner />}
      <main id="main" className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 sm:px-8">
        <Outlet />
      </main>
    </div>
  );
}

export function PageTitle({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <h1 className="text-2xl font-bold sm:text-3xl">{children}</h1>
      {actions}
    </div>
  );
}

export function NotFound() {
  const { t } = useTranslation();
  return (
    <main id="main" className="mx-auto max-w-md px-4 py-12">
      <h1 className="mb-2 text-2xl font-bold">{t('notFound.title')}</h1>
      <Notice kind="info">{t('notFound.text')}</Notice>
      <p className="mt-4">
        <Link to="/" className="text-blue-800 underline">
          {t('notFound.home')}
        </Link>
      </p>
    </main>
  );
}
